import { cn } from "@scribenote/brian-design-system";
import { type PointerEvent, useId, useMemo, useRef, useState } from "react";

import {
  MAX_BOXES_PER_SPACE,
  type MapDevice,
  type MapDisplays,
  type MapDocument,
  type MapRect,
  type MapScreen,
  type MapSegment,
  type MapSpace,
  DISPLAY_STATUS_LABEL,
  type DisplayStatus,
} from "./clinicMapClient";
import {
  boxFromDrag,
  centre,
  doorsOnBox,
  frameOf,
  type GridPoint,
  type MapFrame,
  halfCell,
  insideSpace,
  joinsOf,
  labelBox,
  onStretch,
  outline,
  overlapsAny,
  sameSegment,
  shifted,
  slide,
  snap,
  screenSpaceAt,
  stretchOf,
  touchesSpace,
  wallCellAt,
} from "./mapGeometry";
import { type RoomNames, screenName, spaceName } from "./mapWords";

export type MapTool = "select" | "space" | "door";

export type MapSelection =
  | { kind: "space"; uuid: string; box: number }
  | { kind: "door"; uuid: string }
  | { kind: "device"; instanceId: string }
  | { kind: "screen"; displayUuid: string }
  | null;

type Drag =
  | { kind: "draw"; start: GridPoint; end: GridPoint }
  | {
      kind: "box";
      space: string;
      box: number;
      start: GridPoint;
      orig: MapRect;
      resize: boolean;
      /** Where the box's doors, devices and screens were when the drag began. */
      doors: { uuid: string; segment: MapSegment }[];
      devices: { instanceId: string; x: number; y: number }[];
      screens: { displayUuid: string; x: number; y: number }[];
    }
  | {
      kind: "door";
      uuid: string;
      start: GridPoint;
      orig: MapSegment;
      stretch: MapSegment | null;
    }
  | { kind: "device"; instanceId: string; start: GridPoint; orig: MapDevice }
  | { kind: "screen"; displayUuid: string; start: GridPoint; orig: MapScreen };

/** Hit targets carry their kind, id and box in separate attributes: ids are free text. */
const hit = (kind: string, id: string, box?: number | null) => ({
  "data-hit": kind,
  "data-hit-id": id,
  "data-hit-box": box ?? undefined,
});

const replaceBox = (
  doc: MapDocument,
  space: string,
  index: number,
  box: MapRect,
): MapDocument => ({
  ...doc,
  spaces: doc.spaces.map((s) =>
    s.uuid === space
      ? { ...s, boxes: s.boxes.map((b, i) => (i === index ? box : b)) }
      : s,
  ),
});

const inGrid = (doc: MapDocument, r: MapRect) =>
  r.x >= 0 && r.y >= 0 && r.x + r.w <= doc.cols && r.y + r.h <= doc.rows;

const sameRect = (a: MapRect, b: MapRect) =>
  a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

const line = (s: MapSegment) => ({ x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2 });

/** Facing `heading` degrees clockwise from up, `fov` wide. */
const wedge = (device: MapDevice, radius: number) => {
  const at = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return {
      x: device.x + radius * Math.sin(rad),
      y: device.y - radius * Math.cos(rad),
    };
  };
  const from = at(device.heading_deg - device.fov_deg / 2);
  const to = at(device.heading_deg + device.fov_deg / 2);
  return `M ${device.x} ${device.y} L ${from.x} ${from.y} A ${radius} ${radius} 0 0 1 ${to.x} ${to.y} Z`;
};

const SCREEN_COLOUR: Record<DisplayStatus, string> = {
  online: "var(--color-ink-working)",
  offline: "var(--color-ink-declined)",
  not_paired: "var(--color-annotation)",
};

const SCREEN_STATUSES = Object.keys(SCREEN_COLOUR) as DisplayStatus[];

/** One fading glow per status; stops can't take the colour of the shape using them. */
const ScreenGlows = ({ id }: { id: (status: DisplayStatus) => string }) => (
  <defs>
    {SCREEN_STATUSES.map((status) => (
      <linearGradient key={status} id={id(status)} x1={0} y1={1} x2={0} y2={0}>
        <stop offset={0} stopColor={SCREEN_COLOUR[status]} stopOpacity={0.6} />
        <stop offset={1} stopColor={SCREEN_COLOUR[status]} stopOpacity={0} />
      </linearGradient>
    ))}
  </defs>
);

/** A flat panel across `heading`, glowing out of the side it faces. */
const ScreenMark = ({
  screen,
  displays,
  glowId,
  selected,
  movable,
}: {
  screen: MapScreen;
  displays: MapDisplays;
  glowId: (status: DisplayStatus) => string;
  selected: boolean;
  movable: boolean;
}) => {
  const display = displays.get(screen.display_uuid);
  const status = display?.status ?? "offline";
  const { x, y } = screen;
  return (
    <g
      {...hit("screen", screen.display_uuid)}
      transform={`rotate(${screen.heading_deg} ${x} ${y})`}
      className={cn(movable && "cursor-move")}
    >
      <title>
        {display
          ? `${display.name} · ${DISPLAY_STATUS_LABEL[display.status]}`
          : screenName(display)}
      </title>
      <path
        d={`M ${x - 0.7} ${y - 0.2} L ${x + 0.7} ${y - 0.2} L ${x + 1} ${y - 0.95} L ${x - 1} ${y - 0.95} Z`}
        fill={`url(#${glowId(status)})`}
        className="pointer-events-none"
      />
      <rect
        x={x - 0.75}
        y={y - 0.2}
        width={1.5}
        height={0.4}
        rx={0.08}
        fill={SCREEN_COLOUR[status]}
        vectorEffect="non-scaling-stroke"
        strokeWidth={selected ? 2 : 1}
        className={selected ? "stroke-accent" : "stroke-ink"}
      />
    </g>
  );
};

/** Drawn after the devices, so a camera's wedge never covers a room's name. */
const SpaceLabel = ({
  space,
  rooms,
}: {
  space: MapSpace;
  rooms: RoomNames;
}) => {
  const label = centre(labelBox(space));
  return (
    <text
      x={label.x}
      y={label.y}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={0.7}
      className={cn(
        "pointer-events-none font-body select-none",
        space.room_uuid || space.area ? "fill-ink" : "fill-annotation",
      )}
    >
      {spaceName(space, rooms)}
    </text>
  );
};

const SpaceShape = ({
  space,
  selectedBox,
}: {
  space: MapSpace;
  /** Set when the space is selected: which of its boxes. */
  selectedBox: number | null;
}) => {
  const selected = selectedBox !== null;
  const handle = selected ? space.boxes[selectedBox] : undefined;
  return (
    <g>
      {space.boxes.map((box, index) => (
        <rect
          key={index}
          x={box.x}
          y={box.y}
          width={box.w}
          height={box.h}
          {...hit("space", space.uuid, index)}
          className={cn(
            space.room_uuid ? "fill-card" : "fill-recess",
            selected && "fill-tint-accent",
          )}
        />
      ))}
      {outline(space).map((wall, index) => (
        <line
          key={index}
          {...line(wall)}
          vectorEffect="non-scaling-stroke"
          strokeWidth={selected ? 3 : 2}
          strokeLinecap="square"
          className={cn(
            "pointer-events-none",
            selected ? "stroke-accent" : "stroke-ink",
          )}
        />
      ))}
      {handle && (
        <rect
          x={handle.x + handle.w - 0.3}
          y={handle.y + handle.h - 0.3}
          width={0.6}
          height={0.6}
          {...hit("resize", space.uuid, selectedBox)}
          className="cursor-nwse-resize fill-accent"
        />
      )}
    </g>
  );
};

/**
 * The map on a grid, in grid units. With `onChange` it edits: the select tool
 * moves boxes, doors along their wall, devices and screens; the space tool
 * draws boxes. An exam-room display's screen stays in its room's space.
 * The door tool reports the wall cell clicked to `onWallCell`, limited to
 * `allowedStretches` when given.
 */
export const MapCanvas = ({
  doc,
  rooms,
  tool = "select",
  selection = null,
  onSelect,
  onChange,
  addBoxTo = null,
  onRefuse,
  onWallCell,
  allowedStretches,
  labelled = true,
  frame = frameOf(doc),
  displays,
  className,
}: {
  doc: MapDocument;
  rooms: RoomNames;
  /** Names and status for `doc.screens`, which are drawn only with it. */
  displays?: MapDisplays;
  tool?: MapTool;
  selection?: MapSelection;
  onSelect?: (selection: MapSelection) => void;
  onChange?: (doc: MapDocument) => void;
  /** New boxes join this space instead of starting one. */
  addBoxTo?: string | null;
  /** Explains a refused box (overlap, not touching). */
  onRefuse?: (why: string) => void;
  onWallCell?: (cell: MapSegment) => void;
  allowedStretches?: readonly MapSegment[];
  /** False when the caller draws the space names itself (the live map). */
  labelled?: boolean;
  frame?: MapFrame;
  className?: string;
}) => {
  const svg = useRef<SVGSVGElement>(null);
  const glowPrefix = useId().replace(/[^\w-]/g, "");
  const glowId = (status: DisplayStatus) => `${glowPrefix}-glow-${status}`;
  const [drag, setDrag] = useState<Drag | null>(null);
  const [hover, setHover] = useState<MapSegment | null>(null);
  const joins = useMemo(() => joinsOf(doc), [doc]);
  const grid = useMemo(() => {
    const columns = Array.from(
      { length: doc.cols + 1 },
      (_, x) => `M ${x} 0 V ${doc.rows}`,
    );
    const rows = Array.from(
      { length: doc.rows + 1 },
      (_, y) => `M 0 ${y} H ${doc.cols}`,
    );
    return [...columns, ...rows].join(" ");
  }, [doc.cols, doc.rows]);
  const wallTool = tool === "door" && onWallCell !== undefined;

  const at = (event: PointerEvent): GridPoint | null => {
    const ctm = svg.current?.getScreenCTM();
    if (!ctm) return null;
    const p = new DOMPoint(event.clientX, event.clientY).matrixTransform(
      ctm.inverse(),
    );
    return { x: p.x, y: p.y };
  };

  const wallCell = (p: GridPoint) => {
    const cell = wallCellAt(doc, p);
    return cell &&
      (!allowedStretches || allowedStretches.some((s) => onStretch(cell, s)))
      ? cell
      : null;
  };

  const addBox = (box: MapRect, change: (doc: MapDocument) => void) => {
    if (overlapsAny(doc, box)) {
      onRefuse?.("A box can't overlap another one.");
      return;
    }
    const target = doc.spaces.find((s) => s.uuid === addBoxTo);
    if (target) {
      if (target.boxes.length >= MAX_BOXES_PER_SPACE) {
        onRefuse?.(`A space has at most ${MAX_BOXES_PER_SPACE} boxes.`);
        return;
      }
      if (!touchesSpace(box, target)) {
        onRefuse?.(
          `A box added to ${spaceName(target, rooms)} has to share a wall with it.`,
        );
        return;
      }
      change({
        ...doc,
        spaces: doc.spaces.map((s) =>
          s.uuid === target.uuid ? { ...s, boxes: [...s.boxes, box] } : s,
        ),
      });
      onSelect?.({
        kind: "space",
        uuid: target.uuid,
        box: target.boxes.length,
      });
      return;
    }
    const uuid = crypto.randomUUID();
    change({ ...doc, spaces: [...doc.spaces, { uuid, boxes: [box] }] });
    onSelect?.({ kind: "space", uuid, box: 0 });
  };

  const down = (event: PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    const p = at(event);
    if (!p) return;
    if (wallTool) {
      const cell = wallCell(p);
      if (cell) onWallCell(cell);
      return;
    }
    if (!onChange) return;
    const target = (event.target as Element).closest("[data-hit]");
    const kind = target?.getAttribute("data-hit");
    const id = target?.getAttribute("data-hit-id");
    const index = target?.getAttribute("data-hit-box");
    event.currentTarget.setPointerCapture(event.pointerId);

    if (tool === "space") {
      const start = snap(p, doc);
      setDrag({ kind: "draw", start, end: start });
      return;
    }
    if (kind === "door" && id) {
      const door = doc.doors.find((d) => d.uuid === id);
      if (!door) return;
      onSelect?.({ kind: "door", uuid: id });
      setDrag({
        kind: "door",
        uuid: id,
        start: p,
        orig: door.segment,
        stretch: stretchOf(doc, door),
      });
      return;
    }
    if (kind === "device" && id) {
      const device = doc.devices.find((d) => d.instance_id === id);
      if (!device) return;
      onSelect?.({ kind: "device", instanceId: id });
      setDrag({ kind: "device", instanceId: id, start: p, orig: device });
      return;
    }
    if (kind === "screen" && id) {
      const screen = doc.screens.find((s) => s.display_uuid === id);
      if (!screen) return;
      onSelect?.({ kind: "screen", displayUuid: id });
      setDrag({ kind: "screen", displayUuid: id, start: p, orig: screen });
      return;
    }
    if ((kind === "space" || kind === "resize") && id && index != null) {
      const box = Number(index);
      const orig = doc.spaces.find((s) => s.uuid === id)?.boxes[box];
      if (!orig) return;
      onSelect?.({ kind: "space", uuid: id, box });
      const inside = (d: MapDevice | MapScreen) =>
        d.space_uuid === id &&
        orig.x <= d.x &&
        d.x <= orig.x + orig.w &&
        orig.y <= d.y &&
        d.y <= orig.y + orig.h;
      setDrag({
        kind: "box",
        space: id,
        box,
        start: p,
        orig,
        resize: kind === "resize",
        doors: doorsOnBox(doc, orig).map((d) => ({
          uuid: d.uuid,
          segment: d.segment,
        })),
        devices: doc.devices
          .filter(inside)
          .map((d) => ({ instanceId: d.instance_id, x: d.x, y: d.y })),
        screens: doc.screens
          .filter(inside)
          .map((s) => ({ displayUuid: s.display_uuid, x: s.x, y: s.y })),
      });
      return;
    }
    onSelect?.(null);
  };

  const move = (event: PointerEvent<SVGSVGElement>) => {
    const p = at(event);
    if (!p) return;
    if (wallTool) {
      const cell = wallCell(p);
      if (!sameSegment(cell, hover)) setHover(cell);
      return;
    }
    if (!drag || !onChange) return;
    if (drag.kind === "draw") {
      const end = snap(p, doc);
      if (end.x !== drag.end.x || end.y !== drag.end.y)
        setDrag({ ...drag, end });
      return;
    }
    const dx = Math.round(p.x - drag.start.x);
    const dy = Math.round(p.y - drag.start.y);
    switch (drag.kind) {
      case "box": {
        const next = drag.resize
          ? {
              ...drag.orig,
              w: Math.max(1, drag.orig.w + dx),
              h: Math.max(1, drag.orig.h + dy),
            }
          : { ...drag.orig, x: drag.orig.x + dx, y: drag.orig.y + dy };
        const current = doc.spaces.find((s) => s.uuid === drag.space)?.boxes[
          drag.box
        ];
        if (
          !current ||
          sameRect(current, next) ||
          !inGrid(doc, next) ||
          overlapsAny(doc, next, current)
        )
          return;
        const moved = replaceBox(doc, drag.space, drag.box, next);
        if (drag.resize) {
          onChange(moved);
          return;
        }
        const doors = new Map(
          drag.doors.map((d) => [d.uuid, shifted(d.segment, dx, dy)]),
        );
        const devices = new Map(
          drag.devices.map((d) => [d.instanceId, { x: d.x + dx, y: d.y + dy }]),
        );
        const screens = new Map(
          drag.screens.map((s) => [
            s.displayUuid,
            { x: s.x + dx, y: s.y + dy },
          ]),
        );
        onChange({
          ...moved,
          doors: moved.doors.map((d) => {
            const segment = doors.get(d.uuid);
            return segment ? { ...d, segment } : d;
          }),
          devices: moved.devices.map((d) => ({
            ...d,
            ...devices.get(d.instance_id),
          })),
          screens: moved.screens.map((s) => ({
            ...s,
            ...screens.get(s.display_uuid),
          })),
        });
        return;
      }
      case "door": {
        // A door off every wall moves freely, so it can be put back on one.
        const segment = drag.stretch
          ? slide(
              drag.orig,
              drag.orig.y1 === drag.orig.y2 ? dx : dy,
              drag.stretch,
            )
          : shifted(drag.orig, dx, dy);
        if (
          Math.min(segment.x1, segment.y1) < 0 ||
          segment.x2 > doc.cols ||
          segment.y2 > doc.rows
        )
          return;
        const current = doc.doors.find((d) => d.uuid === drag.uuid);
        if (current && !sameSegment(current.segment, segment))
          onChange({
            ...doc,
            doors: doc.doors.map((d) =>
              d.uuid === drag.uuid ? { ...d, segment } : d,
            ),
          });
        return;
      }
      case "device": {
        const next = {
          ...drag.orig,
          x: halfCell(drag.orig.x + p.x - drag.start.x),
          y: halfCell(drag.orig.y + p.y - drag.start.y),
        };
        const current = doc.devices.find(
          (d) => d.instance_id === drag.instanceId,
        );
        const space = doc.spaces.find((s) => s.uuid === next.space_uuid);
        if (
          current &&
          (current.x !== next.x || current.y !== next.y) &&
          space &&
          insideSpace(next, space)
        )
          onChange({
            ...doc,
            devices: doc.devices.map((d) =>
              d.instance_id === drag.instanceId ? next : d,
            ),
          });
        return;
      }
      case "screen": {
        const point = {
          x: halfCell(drag.orig.x + p.x - drag.start.x),
          y: halfCell(drag.orig.y + p.y - drag.start.y),
        };
        const current = doc.screens.find(
          (s) => s.display_uuid === drag.displayUuid,
        );
        if (!current || (current.x === point.x && current.y === point.y))
          return;
        const space = screenSpaceAt(
          doc,
          displays?.get(drag.displayUuid)?.home_room_uuid ?? null,
          point,
          current.space_uuid,
        );
        if (!space) return;
        onChange({
          ...doc,
          screens: doc.screens.map((s) =>
            s.display_uuid === drag.displayUuid
              ? { ...s, ...point, space_uuid: space.uuid }
              : s,
          ),
        });
        return;
      }
    }
  };

  const up = () => {
    if (drag?.kind === "draw" && onChange) {
      const box = boxFromDrag(drag.start, drag.end);
      if (box) addBox(box, onChange);
    }
    setDrag(null);
  };

  const preview =
    drag?.kind === "draw" ? boxFromDrag(drag.start, drag.end) : null;

  return (
    <svg
      ref={svg}
      viewBox={`${frame.x} ${frame.y} ${frame.width} ${frame.height}`}
      role="group"
      aria-label="Clinic map"
      className={cn(
        "block h-auto w-full touch-none select-none",
        tool !== "select" && (onChange || wallTool) && "cursor-crosshair",
        className,
      )}
      style={{ aspectRatio: `${frame.width} / ${frame.height}` }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={() => setDrag(null)}
      onPointerLeave={() => setHover(null)}
    >
      <rect
        x={0}
        y={0}
        width={doc.cols}
        height={doc.rows}
        className="fill-paper"
      />
      {onChange && (
        <path
          d={grid}
          vectorEffect="non-scaling-stroke"
          strokeWidth={0.5}
          className="pointer-events-none stroke-rule"
        />
      )}
      {doc.spaces.map((space) => (
        <SpaceShape
          key={space.uuid}
          space={space}
          selectedBox={
            selection?.kind === "space" && selection.uuid === space.uuid
              ? selection.box
              : null
          }
        />
      ))}
      {doc.doors.map((door) => {
        const join = joins.get(door.uuid);
        const broken = !join?.ok || (join.to === null && !door.outside);
        const selected =
          selection?.kind === "door" && selection.uuid === door.uuid;
        return (
          <g key={door.uuid}>
            <line
              {...line(door.segment)}
              vectorEffect="non-scaling-stroke"
              strokeWidth={selected ? 9 : 7}
              strokeDasharray={door.kind === "opening" ? "3 3" : undefined}
              className={cn(
                "pointer-events-none",
                broken
                  ? "stroke-ink-declined"
                  : join.to === null
                    ? "stroke-gold"
                    : "stroke-sage",
                selected && "stroke-accent",
              )}
            />
            <line
              {...line(door.segment)}
              {...hit("door", door.uuid)}
              vectorEffect="non-scaling-stroke"
              strokeWidth={16}
              className={cn(
                "stroke-transparent",
                onChange && tool === "select" && "cursor-grab",
              )}
            />
          </g>
        );
      })}
      {doc.devices.map((device) => {
        const selected =
          selection?.kind === "device" &&
          selection.instanceId === device.instance_id;
        return (
          <g
            key={device.instance_id}
            {...hit("device", device.instance_id)}
            className={cn(onChange && tool === "select" && "cursor-move")}
          >
            <path
              d={wedge(device, 2.2)}
              className={cn(
                "pointer-events-none fill-tint-accent stroke-accent",
                selected && "fill-accent-lite",
              )}
              vectorEffect="non-scaling-stroke"
              strokeWidth={1}
              fillOpacity={0.7}
            />
            <circle
              cx={device.x}
              cy={device.y}
              r={0.45}
              className={selected ? "fill-accent" : "fill-ink"}
            />
          </g>
        );
      })}
      {displays && <ScreenGlows id={glowId} />}
      {displays &&
        doc.screens
          // A deleted display's screen shows only while editing, to be removed.
          .filter((s) => onChange || displays.has(s.display_uuid))
          .map((screen) => (
            <ScreenMark
              key={screen.display_uuid}
              screen={screen}
              displays={displays}
              glowId={glowId}
              selected={
                selection?.kind === "screen" &&
                selection.displayUuid === screen.display_uuid
              }
              movable={onChange !== undefined && tool === "select"}
            />
          ))}
      {labelled &&
        doc.spaces.map((space) => (
          <SpaceLabel key={space.uuid} space={space} rooms={rooms} />
        ))}
      {allowedStretches?.map((stretch, index) => (
        <line
          key={index}
          {...line(stretch)}
          vectorEffect="non-scaling-stroke"
          strokeWidth={8}
          strokeDasharray="6 4"
          className="pointer-events-none stroke-accent"
        />
      ))}
      {hover && (
        <line
          {...line(hover)}
          vectorEffect="non-scaling-stroke"
          strokeWidth={9}
          className="pointer-events-none stroke-accent"
          strokeOpacity={0.6}
        />
      )}
      {preview && (
        <rect
          x={preview.x}
          y={preview.y}
          width={preview.w}
          height={preview.h}
          fill="none"
          vectorEffect="non-scaling-stroke"
          strokeWidth={2}
          strokeDasharray="4 3"
          className="pointer-events-none stroke-accent"
        />
      )}
    </svg>
  );
};
